// @ts-strict-ignore
import {ControlPanel, ControlPanelType, Position, Size} from "@models";
import {NotificationService, SimConfigurationService, SimUserInterfaceService, SimWindowService} from "@services";
import {Component, computed, inject, input, output} from "@angular/core";
import {CdkDragDrop, moveItemInArray} from "@angular/cdk/drag-drop";
import {toSignal} from "@angular/core/rxjs-interop";

export interface PanelOptions {
	activePanel: ControlPanel;
	activeUndockedPanel: ControlPanel;
}

@Component({
	template: "",
	standalone: false,
})
export abstract class PanelBase {
	// I/O
	panelOptions = input.required<PanelOptions>();
	openable = input<boolean>(true);
	inputDraggable = input<boolean>(false, {alias: "draggable"});
	canHidePanel = input<boolean>(true);
	panels = input<ControlPanel[]>([]);
	hiddenPanels = input<ControlPanelType[]>([]);
	hasActiveContentPanels = input<ControlPanelType[]>([]);
	arrowBackActive = input<boolean>(false);
	isDragAndDrop = input<boolean>(true);
	arrowBackClicked = output<void>();

	// Constants
	public settings = SimConfigurationService.settings;
	public hasChangesToSave = false;

	// Properties
	protected temporaryPanelPositions: Map<string, Position> = new Map();

	// Injections
	readonly #userInterfaceService = inject(SimUserInterfaceService);
	readonly #notificationService = inject(NotificationService);
	readonly #windowService = inject(SimWindowService);

	// Derived Properties
	protected isTwoScreen = toSignal(this.#windowService.isTwoScreen, {requireSync: true});
	protected draggable = computed<boolean>(() => this.inputDraggable() && this.isTwoScreen());

	public async togglePanelHidden(panel: ControlPanel) {
		if (this.panelOptions().activePanel?.id === panel.id) {
			this.panelOptions().activePanel = null;
		}

		if (this.panelOptions().activeUndockedPanel?.id === panel.id) {
			this.panelOptions().activeUndockedPanel = null;
		}

		panel.isHidden = !panel.isHidden;

		this.updatePanelHiddenState(panel);
	}

	public async drop(event: CdkDragDrop<ControlPanel>) {
		if (event.isPointerOverContainer) {
			moveItemInArray(this.panels(), event.previousIndex, event.currentIndex);
			this.panelOptions().activePanel = event.container.data;
			await this.savePanelOrder();
		} else {
			await this.undockPane(this.panels()[event.previousIndex], event.distance);
		}
	}

	public async undockPane(panel: ControlPanel, customPosition?: {x: number; y: number}) {
		if (panel && this.draggable()) {
			const boundaries = document.getElementById(panel.id).getBoundingClientRect();

			let newLeftValue;
			let newTopValue;
			if (!customPosition) {
				newLeftValue = boundaries.left + 10;
				newTopValue = boundaries.top + 10;
			} else {
				newLeftValue = boundaries.left + customPosition.x;
				newTopValue = boundaries.top + customPosition.y;
			}

			if (newTopValue < 0) {
				newTopValue = 0;
			}

			panel.undockedPosition ??= {} as Position;
			panel.undockedPosition.left = newLeftValue;
			panel.undockedPosition.top = newTopValue;

			panel.undockedSize ??= {} as Size;
			panel.undockedSize.width = boundaries.width;
			panel.undockedSize.height = 400;

			if (this.panelOptions().activePanel?.id === panel.id) {
				this.panelOptions().activePanel = null;
			}

			panel.undocked = true;
			panel.isOpen = true;

			this.panelOptions().activeUndockedPanel = panel;
			await this.updatePanelPositionAndSize(panel);
			this.updatePanelDockedState(panel);
			this.updatePanelOpenState(panel);
		}
	}

	public async savePanelPosition(panel: ControlPanel) {
		if (this.temporaryPanelPositions.has(panel.id)) {
			const tempPanel = JSON.parse(JSON.stringify(panel));

			const tempPosition = this.temporaryPanelPositions.get(panel.id);

			tempPanel.undockedPosition.left = tempPosition.left;
			tempPanel.undockedPosition.top = tempPosition.top;

			this.temporaryPanelPositions.delete(panel.id);
			await this.updatePanelPositionAndSize(tempPanel);
		} else {
			await this.updatePanelPositionAndSize(panel);
		}
	}

	public async savePanelOrder() {
		if (!this.panels()?.length) {
			return;
		}

		const controlPanelIds = this.panels()
			.filter((x) => !x.isHidden)
			.map((x) => x.id);
		await this.#userInterfaceService.setPanelOrder({
			controlPanelIds,
		});
	}

	public isHidden(panelIndex: number) {
		const panelTypToCheck: ControlPanelType = this.panels()[panelIndex].type;
		if (this.hiddenPanels() && this.hiddenPanels().length > 0) {
			return this.hiddenPanels().some((value) => value !== panelTypToCheck);
		} else {
			return true;
		}
	}

	protected hasActiveContent(type: ControlPanelType) {
		if (this.hasActiveContentPanels()?.length > 0) {
			return this.hasActiveContentPanels().find((activePanel) => activePanel === type);
		} else {
			return false;
		}
	}

	protected updatePanelOpenState(panel: ControlPanel) {
		this.#userInterfaceService
			.setControlPanelOpenState({
				controlPanelId: panel.id,
				isOpen: panel.isOpen,
			})
			.then((result) => {
				if (!result) {
					this.#notificationService.notifyError(`Could not save the open state of panel '${panel.id}'`);
				}
			});
	}

	protected updatePanelHiddenState(panel: ControlPanel) {
		this.#userInterfaceService
			.setControlPanelHiddenState({
				controlPanelId: panel.id,
				isHidden: panel.isHidden,
			})
			.then((result) => {
				if (!result) {
					this.#notificationService.notifyError(`Could not save the hidden state of panel '${panel.id}'`);
				}
			});
	}

	protected updatePanelDockedState(panel: ControlPanel) {
		this.#userInterfaceService
			.setControlPanelDockedState({
				controlPanelId: panel.id,
				isDocked: !panel.undocked,
			})
			.then((result) => {
				if (!result) {
					this.#notificationService.notifyError(`Could not save the docked state of panel '${panel.id}'`);
				}
			});
	}

	protected async updatePanelPositionAndSize(panel: ControlPanel) {
		const result = await this.#userInterfaceService.positionControlPanel({
			controlPanelId: panel.id,
			top: panel.undockedPosition.top,
			left: panel.undockedPosition.left,

			x: panel.undockedPosition.x,
			y: panel.undockedPosition.y,

			width: panel.undockedSize.width,
			height: panel.undockedSize.height,
		});

		if (!result) {
			this.#notificationService.notifyError(`Could not save position and size of panel '${panel.id}'`);
		} else {
			this.hasChangesToSave = false;
		}
	}
}
